import { useEffect, useRef, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useValidateUsageSourceMutation } from "../../../api/usageSourceApi";

const VALIDATABLE_ORIGINS = ["MongoDB", "GSheets", "MySQL"];

/**
 * NEW - no direct precedent in Company/Markets/Billing Units. Owns the
 * source-validation flow: MongoDB/MySQL/GSheets origins must be validated
 * (via `validateUsageSource`, which returns a database list) before the
 * form can submit; "API LINK" (and, per the old code's exact check,
 * only "API LINK" - see docs/known-issues.md for the "Get API Link"
 * origin ambiguity this wasn't able to resolve) skips validation entirely.
 *
 * Ported from the old modal's inline isValidated/isReValidate/
 * databaseOptions state + validateUrl handler + two reset-on-origin-change
 * effects (one per mode, collapsed here into one skip-first-run-then-reset
 * effect - safe for both modes since create mode's initial state is
 * already "not validated", so a harmless no-op reset on the very first
 * origin selection doesn't change behavior).
 *
 * Does NOT port the old `isLoading` flag on databaseSelection
 * (`databaseOptions.length === 0 && isValidated`) - databaseOptions
 * populates synchronously from the same validate-response tick that flips
 * isValidated, so there's no real gap for a spinner to fill (confirmed by
 * reading the old validateUrl handler, not guessed).
 *
 * `databaseOptions` is owned by the CALLER (useUsageSourceFormState), not
 * here - it has to exist before useDynamicForm does, since it feeds
 * useUsageSourceFormSchema's item-list injection, which runs before
 * useDynamicForm produces the `handleChange`/`watch` this hook needs.
 * Lifting just that one piece of state up avoids a circular dependency
 * between "schema needs databaseOptions" and "databaseOptions setter
 * needs handleChange, which needs the schema".
 */
export function useUsageSourceValidation({ mode, rowData, originSourceValue, handleChange, databaseOptions, setDatabaseOptions }) {
  const { showToast } = useToast();
  const [validateUsageSourceApiCall, { isLoading: isValidating }] = useValidateUsageSourceMutation();

  const [isValidated, setIsValidated] = useState(() => (mode === "edit" ? rowData?.isValidated || false : false));
  const [isReValidate, setIsReValidate] = useState(mode === "edit");

  const isFirstOriginRun = useRef(true);
  useEffect(() => {
    if (isFirstOriginRun.current) {
      isFirstOriginRun.current = false;
      return;
    }
    setIsValidated(false);
    setDatabaseOptions([]);
    handleChange("isValidated", false);
    handleChange("databaseSelection", "");
    if (mode === "edit") setIsReValidate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originSourceValue]);

  async function validateUrl(formData) {
    try {
      const response = await validateUsageSourceApiCall({
        ...formData,
        url: originSourceValue === "GSheets" ? formData.googleSheetUrl : formData.mongoDbUrl,
      });

      if (response.data?.success) {
        const dbOptions = response.data.response?.dbs?.map((db) => db.name) || [];
        setDatabaseOptions(dbOptions);
        setIsValidated(true);
        handleChange("isValidated", true);
        if (mode === "edit") setIsReValidate(false);
        showToast(response.data.message || "Validation successful", "success");
      } else {
        showToast(response.data?.message || "Validation failed", "error");
        setIsValidated(false);
        handleChange("isValidated", false);
        setDatabaseOptions([]);
      }
    } catch (error) {
      showToast("Validation failed. Please try again.", "error");
      setIsValidated(false);
      handleChange("isValidated", false);
      setDatabaseOptions([]);
    }
  }

  const shouldShowValidationButton = !isValidated && VALIDATABLE_ORIGINS.includes(originSourceValue);
  const requiresValidation = originSourceValue !== "API LINK";

  return {
    isValidated,
    isReValidate,
    databaseOptions,
    isValidating,
    validateUrl,
    shouldShowValidationButton,
    requiresValidation,
  };
}
